import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtPct } from "@/lib/api";
import PageHeader, { Card } from "@/components/PageHeader";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const toCSV = (rows, headers) => {
  const head = headers.map((h) => h.label).join(",");
  const body = rows.map((r) => headers.map((h) => {
    const v = r[h.key];
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  }).join(",")).join("\n");
  return head + "\n" + body;
};

const downloadCSV = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

export default function Reports() {
  const [pnl, setPnl] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [auctions, setAuctions] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = async () => {
    const params = {};
    if (from) params.from_date = from;
    if (to) params.to_date = to;
    const [p, v, a] = await Promise.all([
      api.get("/reports/pnl", { params }),
      api.get("/reports/vehicles"),
      api.get("/reports/auction"),
    ]);
    setPnl(p.data); setVehicles(v.data); setAuctions(a.data);
  };

  useEffect(() => {
    api.get("/reports/pnl").then((r) => setPnl(r.data));
    api.get("/reports/vehicles").then((r) => setVehicles(r.data));
    api.get("/reports/auction").then((r) => setAuctions(r.data));
  }, []);

  return (
    <div data-testid="reports-page">
      <PageHeader overline="Analytics" title="Financial Reports" description="P&L, per-vehicle profit, auction performance — filterable + exportable." />

      {/* Filters */}
      <Card className="p-5 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <div><Label>From</Label><Input data-testid="r-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-lg" /></div>
          <div><Label>To</Label><Input data-testid="r-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-lg" /></div>
          <Button data-testid="r-apply" onClick={load} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Apply Filter</Button>
        </div>
      </Card>

      {/* P&L */}
      {pnl && (
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Profit & Loss</div>
              <h3 className="font-display text-2xl font-bold mt-1">P&L Statement</h3>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              ["Revenue", pnl.revenue], ["COGS", pnl.cogs], ["Gross Profit", pnl.gross_profit],
              ["Operating Expenses", pnl.operating_expenses], ["Net Profit", pnl.net_profit],
            ].map(([l, v], i) => (
              <div key={l}>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">{l}</div>
                <div className={`font-display text-2xl font-bold mt-1 ${i === 4 ? (v >= 0 ? "text-emerald-600" : "text-red-600") : ""}`}>{fmtMoney(v)}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Vehicle profit report */}
      <Card className="mb-6 overflow-x-auto">
        <div className="p-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Per Vehicle</div>
            <h3 className="font-display text-2xl font-bold mt-1">Vehicle Profit Report</h3>
          </div>
          <Button
            data-testid="export-vehicles-csv"
            onClick={() => downloadCSV("vehicle_profit.csv", toCSV(vehicles, [
              { key: "stock", label: "Stock" }, { key: "vehicle", label: "Vehicle" },
              { key: "purchase_price", label: "Purchase" }, { key: "total_investment", label: "Investment" },
              { key: "sale_price", label: "Sale" }, { key: "gross_profit", label: "Profit" }, { key: "roi_pct", label: "ROI %" },
              { key: "status", label: "Status" },
            ]))}
            className="bg-secondary border border-border hover:bg-secondary/80 text-foreground rounded-lg"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["Stock", "Vehicle", "Investment", "Sale", "Gross Profit", "ROI", "Status"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {vehicles.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground">No data.</td></tr>}
            {vehicles.map((v) => (
              <tr key={v.id} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3 font-mono text-xs">{v.stock}</td>
                <td className="px-4 py-3 font-medium">{v.vehicle}</td>
                <td className="px-4 py-3">{fmtMoney(v.total_investment)}</td>
                <td className="px-4 py-3">{v.sale_price !== null ? fmtMoney(v.sale_price) : "—"}</td>
                <td className={`px-4 py-3 font-semibold ${v.gross_profit === null ? "" : v.gross_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{v.gross_profit === null ? "—" : fmtMoney(v.gross_profit)}</td>
                <td className="px-4 py-3">{v.roi_pct === null ? "—" : fmtPct(v.roi_pct)}</td>
                <td className="px-4 py-3 capitalize">{(v.status || "").replace(/_/g, " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Auction performance */}
      <Card className="overflow-x-auto">
        <div className="p-5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Sources</div>
            <h3 className="font-display text-2xl font-bold mt-1">Auction Performance</h3>
          </div>
          <Button
            data-testid="export-auctions-csv"
            onClick={() => downloadCSV("auction_performance.csv", toCSV(auctions, [
              { key: "house", label: "House" }, { key: "count", label: "Vehicles" },
              { key: "spend", label: "Total Spend" }, { key: "profit", label: "Realized Profit" },
            ]))}
            className="bg-secondary border border-border hover:bg-secondary/80 text-foreground rounded-lg"
          >
            <Download size={14} className="mr-2" /> Export CSV
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-muted-foreground">
            <tr>{["House", "Vehicles", "Total Spend", "Realized Profit"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {auctions.map((a) => (
              <tr key={a.house} className="border-t border-border hover:bg-secondary/40">
                <td className="px-4 py-3 font-medium">{a.house}</td>
                <td className="px-4 py-3">{a.count}</td>
                <td className="px-4 py-3">{fmtMoney(a.spend)}</td>
                <td className={`px-4 py-3 font-semibold ${a.profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(a.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
